/**
 * Mientras el asistente trabaja (D56 de `conversaciones-agente-ia`) y la transcripción de los audios
 * (D57). La lógica sin React: qué decir, dónde ponerlo, y cómo cae un `message:transcript` en el hilo.
 *
 * El conversation-service manda la lista entera de lo que el asistente está haciendo en la
 * conversación —`agent:activity` y `GET …/agent-activity`—: el último que llega gana, y una lista
 * vacía es que terminó. Nada de esto lleva texto del cliente.
 */
import type { TimelineRow } from "@/services/conversations.api";

export type EstadoDeActividad =
  | "esperando_audio"
  | "esperando"
  | "en_cola"
  | "trabajando";

export interface ActividadDelAsistente {
  outboxId: string;
  eventType: "note.mention" | "message.inbound";
  noteId: string | null;
  estado: EstadoDeActividad;
  paso: string | null;
  desde: string;
  agentName: string | null;
}

export type EstadoDeTranscripcion = "pending" | "done" | "failed" | null;

/** «Asistente de prueba tecnopor está buscando productos». Sin puntos: los pone la animación. */
export function textoDeActividad(a: ActividadDelAsistente): string {
  const nombre = a.agentName?.trim() || "El asistente";
  if (a.estado === "esperando_audio")
    return `${nombre} está esperando que se transcriba el audio`;
  if (a.eventType === "message.inbound") {
    if (a.estado === "esperando")
      return `${nombre} va a responder en unos segundos`;
    return a.paso
      ? `${nombre} está respondiendo: ${a.paso}`
      : `${nombre} está respondiendo`;
  }
  return `${nombre} está ${a.paso ?? "pensando"}`;
}

/** Lo que va debajo de cada nota que arrobó al asistente, por `noteId`. */
export function actividadPorNota(
  items: ActividadDelAsistente[],
): Map<string, ActividadDelAsistente> {
  const m = new Map<string, ActividadDelAsistente>();
  for (const a of items)
    if (a.eventType === "note.mention" && a.noteId) m.set(a.noteId, a);
  return m;
}

/** Lo que va al pie del chat: el asistente respondiéndole al cliente en `auto`. */
export function actividadAlPie(
  items: ActividadDelAsistente[],
): ActividadDelAsistente | null {
  return items.find((a) => a.eventType === "message.inbound") ?? null;
}

/**
 * `message:transcript`: el audio que corresponde pasa a `pending`, recibe el texto o queda `failed`.
 * Si el mensaje no está en pantalla no se agrega nada: el timeline lo trae con su estado.
 *
 * Un `pending` nunca pisa un `done`: la respuesta del pedido puede llegar después del socket que ya
 * trajo el texto, y el audio quedaría «Transcribiendo…» para siempre, sin botón para reintentar.
 */
export function aplicarTranscripcion(
  rows: TimelineRow[],
  t: {
    messageId: string;
    transcript: string | null;
    transcriptStatus: EstadoDeTranscripcion;
  },
): TimelineRow[] {
  let cambio = false;
  const nuevas = rows.map((r) => {
    if (r.kind !== "message" || r.payload.messageId !== t.messageId) return r;
    if (
      t.transcriptStatus === "pending" &&
      r.payload.transcriptStatus === "done"
    )
      return r;
    cambio = true;
    return {
      ...r,
      payload: {
        ...r.payload,
        transcript: t.transcript,
        transcriptStatus: t.transcriptStatus,
      },
    };
  });
  return cambio ? nuevas : rows;
}
